import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
  signal,
} from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { UiPanelComponent } from '@template-ui/components';
import {
  WorkflowDefinitionDocument,
  WorkflowProcessDataField,
} from './platform-workflow-definition-lifecycle.models';
import { processDataDependencyErrors, processDataErrors, processDataFieldIssues, processDataShapeError, supportsWorkflowProcessData } from './workflow-process-data';
import type { WorkflowEditorPendingScope, WorkflowEditorBufferState } from './workflow-editor-pending-state';
import { WorkflowOwnerViewState } from './workflow-owner-view-state';
import { applyWorkflowProcessData, type WorkflowProcessDataDraft } from './workflow-process-data-authoring';

export interface WorkflowProcessDataOwnedDocumentChange {
  document: WorkflowDefinitionDocument; owner: string; source: string; view: string | null; draft: WorkflowProcessDataDraft;
}
interface ProcessDataOrigin { owner: string; source: string; view: string | null; generation: number; fieldId: number | null; shape: number; }
type ProcessDataFocusControl = 'name' | 'type' | 'requiredAtStart' | 'key' | 'recordTypeCode';

let processDataEditorIdentity = 0;

@Component({
  selector: 'phms-workflow-process-data-editor',
  standalone: true,
  imports: [UiButtonDirective, UiControlDirective, UiPanelComponent, CommonModule, FormsModule],
  templateUrl: './workflow-process-data-editor.component.html',
  styleUrl: './workflow-process-data-editor.component.scss',
})
export class WorkflowProcessDataEditorComponent implements OnChanges {
  @Input({ required: true }) document!: WorkflowDefinitionDocument;
  @Input() canAuthor = true;
  @Input() interactionBlocked = false;
  @Input() workspaceMode = false;
  @Input() latestDocument: (() => WorkflowDefinitionDocument | null) | null = null;
  @Input() ownerContext: (() => unknown) | null = null;
  @Input() viewContext: (() => unknown) | null = null;
  @Input() active: (() => boolean) | null = null;
  @Input() activeView: (() => string | null) | null = null;
  @Input() activeViewState: string | null | undefined = undefined;
  @Output() documentChange = new EventEmitter<WorkflowDefinitionDocument>();
  @Output() pendingChangesChange = new EventEmitter<boolean>();
  @Output() ownedDocumentChange = new EventEmitter<WorkflowProcessDataOwnedDocumentChange>();
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ownerViews = inject(WorkflowOwnerViewState, { optional: true }) ?? new WorkflowOwnerViewState();
  fields: WorkflowProcessDataField[] = [];
  readonly selectedIndex = signal(0);
  readonly keyDisclosureOpen = signal(false);
  readonly errorId = `workflow-process-data-error-${++processDataEditorIdentity}`;
  private readonly generatedKeyFields = new Set<number>();
  private fieldIds: number[] = [];
  private readonly originalKeys = new Map<number, string>();
  private nextFieldId = 0;
  private focusRequest = 0;
  private original = '';
  private originalContext = '';
  private pending = false;
  private ownerSnapshot = '';
  private bufferNamespace = '';
  private renderedView: string | null = null;
  private generation = 0;
  private readonly fieldShapes = new Map<number, number>();
  private restoring = false;
  error = '';
  types = [
    { code: 'string', label: 'Text' },
    { code: 'integer', label: 'Whole number' },
    { code: 'number', label: 'Number' },
    { code: 'boolean', label: 'Yes / no' },
    { code: 'date', label: 'Date' },
    { code: 'record-reference', label: 'Record reference' },
  ];
  trackField = (index: number) => this.fieldIds[index];
  readonly trackRow = (_index: number, row: { id: number }) => row.id;
  get headingId(): string { return `${this.errorId}-heading`; }
  get draftSnapshot(): WorkflowProcessDataDraft { return { fields: structuredClone(this.fields) }; }
  get rows() {
    const origin = this.origin(null);
    return this.fields.map((field, index) => ({ field, index, id: this.fieldIds[index],
      origin: { ...origin, fieldId: this.fieldIds[index], shape: this.fieldShapes.get(this.fieldIds[index]) ?? 0 } }));
  }
  get selectedEditor() {
    const index = this.selectedIndex(), field = this.fields[index];
    return field ? { field, index, id: this.fieldIds[index], origin: this.origin(this.fieldIds[index]) } : undefined;
  }
  get addOrigin(): ProcessDataOrigin { return this.origin(null); }
  get selectedField(): WorkflowProcessDataField | undefined { return this.fields[this.selectedIndex()]; }
  get keyDisclosureVisible(): boolean { return this.keyDisclosureOpen() || this.controlInvalid('key'); }
  get selectedPanelTitle(): string {
    // UiPanel permits HTML titles; field names stay literal authored text.
    return (typeof this.selectedField?.label === 'string' && this.selectedField.label || 'New shared value').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
  get readOnlyReason(): string {
    return !supportsWorkflowProcessData(this.document?.payloadSchemaVersion)
      ? 'This saved workflow format does not support editing shared data. Its original content is retained.'
      : processDataShapeError(this.document?.processData) ?? '';
  }
  get editable(): boolean { return this.canAuthor && !this.interactionBlocked && !this.readOnlyReason && (!this.workspaceMode || this.ownerCurrent()); }
  get staleSource(): boolean { const document = this.currentDocument(); return this.pending && (!document || !this.ownerCurrent()
    || this.documentContext(document) !== this.originalContext || JSON.stringify(document.processData ?? null) !== this.original); }
  typeLabel(type: WorkflowProcessDataField['type']): string { return this.types.find(item => item.code === type)?.label ?? type; }
  controlInvalid(control: 'label' | 'key' | 'type' | 'recordTypeCode'): boolean {
    try {
    const field = this.selectedField;
    if (!this.error || !field) return false;
    if (processDataFieldIssues(field)[control]) return true;
    if (control === 'key' && this.fields.filter(candidate => candidate.key === field.key).length > 1) return true;
    const originalKey = this.originalKeys.get(this.fieldIds[this.selectedIndex()]);
    const original = this.document.processData?.fields.find(candidate => candidate.key === originalKey);
    if (!original || !processDataDependencyErrors(this.fields, this.document, new Set([original.key, field.key])).length) return false;
    return control === 'key' ? original.key !== field.key : control === 'type' ? original.type !== field.type
      : control === 'recordTypeCode' && original.recordTypeCode !== field.recordTypeCode;
    } catch { return !!this.error; }
  }
  selectField(index: number, origin?: ProcessDataOrigin): void {
    index = this.originIndex(index, origin);
    if (!this.nativeCurrent(origin) || this.interactionBlocked || !this.fields[index]) return;
    this.focusRequest++;
    if (index !== this.selectedIndex()) { this.keyDisclosureOpen.set(false); this.generation++; }
    this.selectedIndex.set(index);
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (!this.workspaceMode) { if (!this.pending) this.reload(); return; }
    const view = this.activeView?.() ?? null;
    if ((this.activeViewState === undefined || this.activeViewState === view) && this.renderedView !== view) {
      this.renderedView = view; this.generation++; this.focusRequest++;
    }
    if (this.pending && changes['document'] && this.viewContext && this.bufferNamespace === this.namespace()
      && this.documentContext(this.document) === this.originalContext && JSON.stringify(this.document.processData ?? null) === this.original)
      this.ownerSnapshot = this.ownerKey();
    if (this.pending && changes['document']) { this.generation++; this.focusRequest++; }
    if (!this.pending && (changes['document'] || changes['workspaceMode'])) this.reload();
    else if (!this.pending && changes['activeViewState'] && this.activeViewState === view) {
      this.ownerSnapshot = this.ownerKey(); this.bufferNamespace = this.namespace();
    }
  }
  private reload(): void {
    const selectedKey = this.selectedField?.key;
    const sameContext = this.originalContext === this.documentContext(this.document);
    const oldFields = this.fields, oldIds = this.fieldIds;
    this.fields = this.readOnlyReason ? [] : structuredClone(this.document?.processData?.fields ?? []);
    this.fieldIds = this.fields.map(field => {
      const matches = sameContext && this.workspaceMode ? oldFields.flatMap((old, index) => old.key === field.key ? [oldIds[index]] : []) : [];
      return matches.length === 1 && this.fields.filter(candidate => candidate.key === field.key).length === 1 ? matches[0] : ++this.nextFieldId;
    });
    this.fieldShapes.clear();
    this.originalKeys.clear();
    this.fields.forEach((field, index) => this.originalKeys.set(this.fieldIds[index], field.key));
    this.generatedKeyFields.clear();
    const matches = this.fields.flatMap((field, index) => field.key === selectedKey ? [index] : []);
    this.selectedIndex.set(sameContext && matches.length === 1 ? matches[0] : this.workspaceMode && sameContext && selectedKey !== undefined ? -1 : 0);
    this.original = JSON.stringify(this.document?.processData ?? null);
    this.originalContext = this.documentContext(this.document);
    this.ownerSnapshot = this.ownerKey(); this.bufferNamespace = this.namespace(); this.generation++; this.focusRequest++;
  }
  hasPendingChanges(): boolean {
    return this.pending;
  }
  private documentContext(document: WorkflowDefinitionDocument): string {
    return JSON.stringify([document.workflowCode, document.definitionVersionCode, document.packReference]);
  }
  /** One schema buffer checks the known retained consumers without claiming a complete usage inventory. */
  pendingEditorScopes(latestDocument: () => WorkflowDefinitionDocument | null = () => this.currentDocument()): readonly WorkflowEditorPendingScope[] {
    return [{
      id: 'process-data-fields', label: 'process data fields',
      state: (): WorkflowEditorBufferState => {
        if (!this.pending) return 'clean';
        const document = latestDocument();
        if (!document || !this.ownerCurrent() || this.documentContext(document) !== this.originalContext || JSON.stringify(document.processData ?? null) !== this.original) return 'stale';
        if (!this.editable || !supportsWorkflowProcessData(document.payloadSchemaVersion) || processDataShapeError(document.processData)) return 'invalid';
        try { applyWorkflowProcessData(document, this.draftSnapshot.fields); return 'valid'; } catch { return 'invalid'; }
      },
      fingerprint: () => {
        const document = latestDocument();
        return JSON.stringify([this.pending, this.original, this.originalContext, this.fields, this.canAuthor, this.interactionBlocked,
          this.ownerSnapshot, this.ownerKey(), this.bufferNamespace, this.namespace(),
          document && [this.documentContext(document), document.processData, document.nodes, document.triggers, document.payloadSchemaVersion]]);
      },
      apply: () => { const document = latestDocument(); return !!document && this.applyPendingChanges(document); },
      discard: () => { const document = latestDocument(); if (document) this.document = document; this.discardPendingChanges(); },
      focus: () => this.focusPendingChanges(),
    }];
  }
  private changed(): void {
    if (!this.pending) this.bufferNamespace = this.namespace();
    this.pending = true;
    this.error = '';
    this.pendingChangesChange.emit(true);
  }
  add(origin?: ProcessDataOrigin): void {
    if (!this.editable || !this.nativeCurrent(origin)) return;
    this.fields = [
      ...this.fields,
      { key: '', label: '', type: 'string', requiredAtStart: false },
    ];
    const id = ++this.nextFieldId;
    this.fieldIds.push(id);
    this.generation++;
    this.generatedKeyFields.add(id);
    this.selectedIndex.set(this.fields.length - 1);
    this.keyDisclosureOpen.set(false);
    this.changed();
    if (this.workspaceMode) this.scheduleFocus(false, 'name', undefined, true); else this.focusField();
  }
  update(index: number, patch: Partial<WorkflowProcessDataField>, origin?: ProcessDataOrigin, model?: NgModel): void {
    index = this.originIndex(index, origin);
    const property = Object.keys(patch)[0] as keyof WorkflowProcessDataField;
    if (!this.editable || !this.nativeCurrent(origin, true) || !this.fields[index] || !this.patchSupported(patch)
      || this.workspaceMode && Object.hasOwn(patch, 'recordTypeCode') && (this.fields[index].type !== 'record-reference'
        || origin && origin.shape !== (this.fieldShapes.get(this.fieldIds[index]) ?? 0))) { this.restore(model, property); return; }
    if (Object.hasOwn(patch, 'key')) this.generatedKeyFields.delete(this.fieldIds[index]);
    this.patchField(index, patch);
  }
  referenceKey(value: string): string {
    return value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }
  updateName(index: number, label: string, origin?: ProcessDataOrigin, model?: NgModel): void {
    index = this.originIndex(index, origin);
    if (!this.editable || !this.nativeCurrent(origin, true) || !this.fields[index] || typeof label !== 'string') { this.restore(model, 'label'); return; }
    const field = this.fields[index];
    const automatic = this.generatedKeyFields.has(this.fieldIds[index]) &&
      (!field.key || field.key === this.referenceKey(field.label));
    this.patchField(index, { label, ...(automatic ? { key: this.referenceKey(label) } : {}) });
  }
  private patchField(index: number, patch: Partial<WorkflowProcessDataField>): void {
    this.fields = this.fields.map((field, i) => i === index ? { ...field, ...patch } : field);
    this.changed();
  }
  updateType(index: number, type: WorkflowProcessDataField['type'], origin?: ProcessDataOrigin, model?: NgModel): void {
    index = this.originIndex(index, origin);
    if (!this.editable || !this.nativeCurrent(origin, true) || !this.fields[index] || !this.types.some(item => item.code === type)) { this.restore(model, 'type'); return; }
    const field = this.fields[index];
    this.patchField(index, { type, recordTypeCode: type === 'record-reference' ? field.recordTypeCode || 'scheduled-proceeding' : null });
    if (type !== field.type) this.fieldShapes.set(this.fieldIds[index], (this.fieldShapes.get(this.fieldIds[index]) ?? 0) + 1);
  }
  remove(index: number, origin?: ProcessDataOrigin): void {
    index = this.originIndex(index, origin);
    if (!this.editable || !this.nativeCurrent(origin, true) || !this.fields[index]) return;
    const remaining = this.fields.filter((_, i) => i !== index);
    const key = this.fields[index].key;
    // Check this row's current and saved identity without letting unrelated invalid rows block removal.
    const affectedKeys = new Set([key, this.originalKeys.get(this.fieldIds[index]) ?? key]);
    this.error = this.dependencyErrors(remaining, this.currentDocument() ?? this.document, affectedKeys).join(' ');
    if (this.error) { this.scheduleFocus(true, 'name', undefined, this.workspaceMode); return; }
    this.fields = remaining;
    this.generatedKeyFields.delete(this.fieldIds[index]);
    this.originalKeys.delete(this.fieldIds[index]);
    this.fieldIds.splice(index, 1);
    this.generation++;
    this.selectedIndex.set(Math.max(0, Math.min(this.selectedIndex() - (index < this.selectedIndex() ? 1 : 0), this.fields.length - 1)));
    this.changed();
  }
  applyPendingChanges(document?: WorkflowDefinitionDocument): boolean {
    if (!this.pending) return true;
    const live = this.currentDocument();
    document ??= this.workspaceMode ? live ?? this.document : this.document;
    if (this.workspaceMode && (!live || JSON.stringify(document) !== JSON.stringify(live))) {
      this.error = 'Shared data changed while you were editing. Your input is retained. Review the current source before applying.';
      this.focusPendingChanges(this.workspaceMode); return false;
    }
    if (!this.ownerCurrent() || this.documentContext(document) !== this.originalContext || JSON.stringify(document.processData ?? null) !== this.original)
      this.error =
        'Shared data changed while you were editing. Your input is retained. Leave this task and use the discard choice to reload the current schema.';
    else this.error = '';
    if (this.error) {
      this.focusPendingChanges(this.workspaceMode);
      return false;
    }
    if (!this.editable || !supportsWorkflowProcessData(document.payloadSchemaVersion) || processDataShapeError(document.processData)) return false;
    let next: WorkflowDefinitionDocument;
    try { next = applyWorkflowProcessData(document, this.draftSnapshot.fields); }
    catch (error) { this.error = error instanceof Error ? error.message : 'Review shared data input.'; this.focusPendingChanges(this.workspaceMode); return false; }
    const owned: WorkflowProcessDataOwnedDocumentChange = { document: next, owner: this.ownerKey(), source: JSON.stringify(document),
      view: this.activeView?.() ?? null, draft: this.draftSnapshot };
    if (this.workspaceMode) {
      // The root owns this write. Keep the raw buffer until its synchronous
      // current-document acknowledgement confirms the exact accepted schema.
      const namespace = this.bufferNamespace;
      this.ownedDocumentChange.emit(owned);
      const acknowledged = this.currentDocument();
      if (owned.owner !== this.ownerKey() || namespace !== this.namespace() || acknowledged === live || JSON.stringify(acknowledged) !== JSON.stringify(next)) {
        this.error = 'Shared data was not applied to the current draft. Your input is retained; review the current owner and source.';
        this.focusPendingChanges(this.workspaceMode); return false;
      }
    }
    this.pending = false;
    this.pendingChangesChange.emit(false);
    this.document = next;
    this.documentChange.emit(this.document);
    // The owner compares the raw actual draft before Apply's existing normalization.
    if (!this.workspaceMode) this.ownedDocumentChange.emit(owned);
    this.fields = structuredClone(next.processData!.fields);
    this.original = JSON.stringify(this.document.processData);
    this.generation++; this.focusRequest++;
    this.generatedKeyFields.clear();
    this.originalKeys.clear();
    this.fields.forEach((field, index) => this.originalKeys.set(this.fieldIds[index], field.key));
    return true;
  }
  discardPendingChanges(): void {
    this.document = this.currentDocument() ?? this.document;
    this.pending = false;
    this.error = '';
    this.reload();
    this.pendingChangesChange.emit(false);
  }
  focusPendingChanges(reveal = false): void {
    if (this.workspaceMode && !this.presentationActive()) return;
    let invalid = -1;
    try { invalid = this.fields.findIndex((field, index) => processDataErrors([field], { ...this.document, nodes: [] }).length > 0
      || this.fields.findIndex(candidate => candidate.key === field.key) !== index); }
    catch { invalid = this.fields.length ? 0 : -1; }
    if (invalid >= 0 && invalid !== this.selectedIndex()) { this.selectedIndex.set(invalid); this.generation++; }
    try {
      if (this.selectedField && (processDataFieldIssues(this.selectedField).key || this.fields.filter(field => field.key === this.selectedField!.key).length > 1))
        this.keyDisclosureOpen.set(true);
    } catch { /* The visible controlled error retains malformed input. */ }
    this.scheduleFocus(true, 'name', undefined, reveal);
  }
  focusField(key?: string, control: ProcessDataFocusControl = 'name', reveal = false): boolean {
    if (this.workspaceMode && !this.presentationActive()) return false;
    this.focusRequest++;
    if (this.workspaceMode && key === undefined) return false;
    if (key === undefined && !this.selectedField && !this.editable) return false;
    if (key !== undefined) {
      if (!key) return false;
      const matches = this.fields.flatMap((field, index) => field.key === key ? [index] : []);
      if (matches.length !== 1) return false;
      if (control === 'recordTypeCode' && this.fields[matches[0]].type !== 'record-reference') return false;
      if (this.selectedIndex() !== matches[0]) { this.selectedIndex.set(matches[0]); this.generation++; }
    }
    if (control === 'key') this.keyDisclosureOpen.set(true);
    this.scheduleFocus(false, control, key, reveal); return true;
  }
  focusAddValue(reveal = false): void { this.scheduleFocus(false, 'add', undefined, reveal); }
  focusHeading(reveal = false): void { this.scheduleFocus(false, 'heading', undefined, reveal); }
  private scheduleFocus(error: boolean, control: ProcessDataFocusControl | 'add' | 'heading' = 'name', exactKey?: string, reveal = false): void {
    const request = ++this.focusRequest, context = this.documentContext(this.document), selectedId = this.fieldIds[this.selectedIndex()],
      owner = this.ownerKey(), source = JSON.stringify(this.currentDocument()), view = this.activeView?.() ?? null, generation = this.generation,
      focused = this.element.nativeElement.ownerDocument.activeElement;
    setTimeout(() => {
      const host = this.element.nativeElement, active = host.ownerDocument.activeElement;
      if (request !== this.focusRequest || this.destroyRef.destroyed || !this.presentationActive() || !host.isConnected
        || context !== this.documentContext(this.document) || selectedId !== this.fieldIds[this.selectedIndex()]
        || owner !== this.ownerKey() || source !== JSON.stringify(this.currentDocument()) || view !== (this.activeView?.() ?? null) || generation !== this.generation
        || exactKey !== undefined && this.fields.filter(field => field.key === exactKey).length !== 1) return;
      if (active !== focused && active !== host.ownerDocument.body && active !== host.ownerDocument.documentElement
        && active?.isConnected && !active.closest('[hidden],[inert]')) return;
      const selector = control === 'requiredAtStart' ? '[data-field-required-start]:not(:disabled)' : control === 'type' ? '[data-field-type]:not(:disabled)'
        : control === 'key' ? '[data-field-key]:not(:disabled)' : control === 'recordTypeCode' ? '[data-field-record-type]:not(:disabled)'
        : control === 'add' ? '[data-add-process-data]:not(:disabled)' : control === 'heading' ? '[data-process-data-heading]' : '[data-field-name]:not(:disabled)';
      const target = (error ? host.querySelector<HTMLElement>('.error') : null) ?? host.querySelector<HTMLElement>(selector)
        ?? (!this.workspaceMode || !this.canAuthor && exactKey !== undefined ? host.querySelector<HTMLElement>('[data-field-detail]') : null)
        ?? host.querySelector<HTMLElement>('[data-process-data-heading]');
      if (!target || target.closest('[hidden],[inert]') || target.matches(':disabled')) return;
      const expected = request + (active === target ? 0 : 1); target.focus({ preventScroll: this.workspaceMode });
      if (reveal && this.workspaceMode && host.ownerDocument.activeElement === target && this.focusRequest === expected && this.presentationActive()
        && owner === this.ownerKey() && source === JSON.stringify(this.currentDocument()) && view === (this.activeView?.() ?? null)
        && generation === this.generation && target.isConnected && !target.closest('[hidden],[inert]')) target.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    });
  }
  @HostListener('focusin') rememberFocus(): void { this.focusRequest++; }
  capturePendingDataInputs(): readonly { name: string; label: string; value: string }[] {
    return this.pending ? [{ name: 'process-data-input', label: 'Unapplied shared data', value: JSON.stringify({ format: 'phms.workflow-process-data-input', version: 1,
      fields: this.draftSnapshot.fields, originalConfiguration: this.original, originalContext: this.originalContext, originalOwner: this.ownerSnapshot,
      originalNamespace: this.bufferNamespace, fieldIds: [...this.fieldIds], originalKeys: [...this.originalKeys], generatedFieldIds: [...this.generatedKeyFields],
      selectedFieldId: this.fieldIds[this.selectedIndex()] ?? null }) }] : [];
  }
  toggleKeyDisclosure(open: boolean, origin: ProcessDataOrigin, details: HTMLDetailsElement): void {
    if (!this.nativeCurrent(origin, true)) {
      const visible = this.keyDisclosureVisible;
      if (details.open !== visible) details.open = visible;
      return;
    }
    this.keyDisclosureOpen.set(open);
  }
  private ownerKey(): string { return JSON.stringify(this.ownerContext?.() ?? null); }
  private currentDocument(): WorkflowDefinitionDocument | null { return this.latestDocument ? this.latestDocument() : this.document; }
  private namespace(): string { return JSON.stringify([this.documentContext(this.document), this.viewContext?.() ?? this.ownerContext?.() ?? null]); }
  private ownerCurrent(): boolean { return !this.workspaceMode || !this.destroyRef.destroyed && this.ownerSnapshot === this.ownerKey() && this.bufferNamespace === this.namespace(); }
  private presentationActive(): boolean {
    const host = this.element.nativeElement;
    return !this.destroyRef.destroyed && !this.ownerViews.focusSuspended() && !host.closest('[hidden],[inert],[aria-hidden="true"]')
      && (!this.workspaceMode || this.ownerCurrent() && (!this.active || this.active())
        && (!this.activeView || this.activeView() !== null && this.activeView() === this.renderedView
          && (this.activeViewState === undefined || this.activeViewState === this.renderedView))
        && JSON.stringify(this.document) === JSON.stringify(this.currentDocument()));
  }
  private origin(fieldId: number | null): ProcessDataOrigin { return { fieldId, generation: this.generation, shape: fieldId === null ? 0 : this.fieldShapes.get(fieldId) ?? 0,
    owner: this.ownerKey(), source: JSON.stringify(this.document), view: this.activeView?.() ?? null }; }
  private originIndex(index: number, origin?: ProcessDataOrigin): number { return this.workspaceMode && origin?.fieldId != null ? this.fieldIds.indexOf(origin.fieldId) : index; }
  private nativeCurrent(origin?: ProcessDataOrigin, selected = false): boolean {
    if (this.restoring) return false;
    if (!this.workspaceMode) return true;
    const document = this.currentDocument();
    return this.presentationActive() && !!document && this.documentContext(document) === this.originalContext
      && JSON.stringify(document.processData ?? null) === this.original
      && (!origin || origin.generation === this.generation && origin.owner === this.ownerKey() && origin.source === JSON.stringify(document)
        && origin.view === (this.activeView?.() ?? null) && (origin.fieldId === null || this.fieldIds.includes(origin.fieldId))
        && (!selected || origin.fieldId === this.fieldIds[this.selectedIndex()]));
  }
  private patchSupported(patch: Partial<WorkflowProcessDataField>): boolean {
    if (!this.workspaceMode) return true;
    return Object.keys(patch).every(key => ['key', 'label', 'type', 'requiredAtStart', 'recordTypeCode'].includes(key)
      && (key === 'requiredAtStart' ? typeof patch[key] === 'boolean' : key === 'recordTypeCode' ? patch[key] == null || typeof patch[key] === 'string'
        : typeof patch[key as keyof WorkflowProcessDataField] === 'string'));
  }
  private restore(model: NgModel | undefined, property: keyof WorkflowProcessDataField): void {
    if (!model || this.restoring) return;
    this.restoring = true;
    try { model.control.setValue(this.selectedField?.[property], { emitEvent: false }); }
    finally { this.restoring = false; }
  }
  private dependencyErrors(fields: WorkflowProcessDataField[], document: WorkflowDefinitionDocument, keys?: ReadonlySet<string>): string[] {
    try { return processDataDependencyErrors(fields, document, keys); }
    catch { return ['Some retained shared-data dependencies are unavailable. Their original settings and your input are preserved.']; }
  }
}
